import React, { useState } from 'react'
import Title from 'shared/components/Title'
import * as R from 'ramda'
import { toast } from 'libs/toast'
import dayjs from 'dayjs'

import { Header, Button, Form } from 'semantic-ui-react'
import { Progress } from 'antd'
import { Container } from 'pages/OnboardingWizard/Container'
import { LeftPane } from 'pages/OnboardingWizard/LeftPane'
import { RightPane } from 'pages/OnboardingWizard/RightPane'
import constants from 'config/constants'
import CompanyInformationForm from 'components/CompanyInformationForm'
import IntegrationForm from 'components/IntegrationForm'
import GoalPreferencesForm from 'components/GoalPreferencesForm'
import OnboardingLoading from 'pages/OnboardingLoading'
import { getStarterItemsQueue } from 'pages/OnboardingLoading/utils'
import BrandOnboardingSettingForm from 'organisms/BrandSettings/BrandOnboardingSettingForm'
import BrandOnboardingPreview from 'organisms/BrandSettings/BrandOnboardingPreview'
import { Transition, Image } from 'semantic-ui-react'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { getPreferenceLogData } from './utils'
import { convertUrlToBase64 } from './utils'
import useAxios from 'hooks/useAxios'

export default function Onboarding({
  preferences,
  brandLogos,
  brandColors,
  startingPreferences,
  saveStarterItems,
  savingStarterItems,
  saveBrandSettings,
  defautlBrand,
  companyId,
  companyName,
}) {
  const [brand, setBrand] = useState({
    name: defautlBrand?.name,
    logoObject: {
      src: null,
      id: null,
    },
    backgroundColor: '#F4F4F4',
    backgroundImage: null,
    accentColor: '#017EFE',
    font: 'Lato',
  })

  const [currentStep, setCurrentStep] = useState(1)
  const [showLoadingPage, setShowLoadingPage] = useState(false)
  const [starterItems, setStarterItems] = useState(false)
  const [successDashboardUrl, setSuccessDashboardUrl] = useState('')
  const [selectedPreferences, setSelectedPreferences] = useState(startingPreferences)
  const saveUserActivityLog = useSaveUserActivityLog()

  const lastStep = 6
  const isPreviewBrand = currentStep == lastStep

  const timezone = dayjs.tz.guess()

  const { fetch: getUser } = useAxios({
    url: `account/users/me/v2/?timezone=${encodeURIComponent(timezone)}`,
    skip: true,
  })

  async function submitPreferenceData() {
    const brandSetting = {
      name: brand.name,
      logo: null,
      background_color: brand.backgroundColor,
      accent_color: brand.accentColor,
      background_image: brand.backgroundImage,
      font: brand.font,
    }
    // convert image_url (from brandfetch) to base64
    if (brand.logoObject.src) {
      try {
        const base64Logo = await convertUrlToBase64(brand.logoObject.src)
        brandSetting['logo'] = base64Logo
      } catch (err) {
        console.error(err)
      }
    }

    // update brand setting
    saveBrandSettings({
      url: `brand/brands/${defautlBrand?.id}`,
      data: { ...brandSetting },
    })

    const prefernceIds = selectedPreferences.map((item) => {
      return item.id
    })

    const preferenceLogs = getPreferenceLogData(selectedPreferences)

    setShowLoadingPage(true)

    saveStarterItems({
      data: { preference_ids: prefernceIds },
    })
      .then((response) => {
        const starterItem = R.path(['data', 'starter_items'], response)
        const successDashboardUrl = R.path(['data', 'home_page_url'], response)
        // Refetch user/me after onboarding to get the latest data before redirect to success dashboard
        // If we don't do this user will have no permission to success dashboard and get redirect to home
        // https://github.com/simplesat/simplesat/issues/8890
        getUser({ key: companyId })

        saveUserActivityLog({
          action: ACTION_NAME.completed_wizard,
          statusCode: response.status,
          requestBody: preferenceLogs,
          target: `com_${companyId}`,
        })

        setStarterItems(getStarterItemsQueue(starterItem))
        setSuccessDashboardUrl(successDashboardUrl)
      })
      .catch((error) => {
        saveUserActivityLog({
          action: ACTION_NAME.completed_wizard,
          statusCode: error.response.status,
          errorResponseBody: preferenceLogs,
          target: `com_${companyId}`,
        })
        console.error(error)
      })
  }

  function redirectToSuccessDashboard() {
    window.location.href = successDashboardUrl
  }

  function handleStepChange(type) {
    if (currentStep === 1) {
      const selectedPreferenceTypes = selectedPreferences.map((item) => {
        return item.type
      })
      if (
        !['industry', 'size', 'country'].every((value) => selectedPreferenceTypes.includes(value))
      ) {
        return toast.warning('Please select industry, company size, and country before proceeding.')
      }
    }

    if (type == 'next') {
      if (isPreviewBrand) {
        submitPreferenceData()
      } else {
        setCurrentStep(currentStep + 1)
      }
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  if (showLoadingPage) {
    return (
      <OnboardingLoading
        apiLoading={savingStarterItems}
        queue={starterItems}
        onComplete={redirectToSuccessDashboard}
      />
    )
  }

  const renderFormComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <React.Fragment>
            <Header as="h1">Tell us a little more about your company</Header>
            <CompanyInformationForm
              selectedItems={selectedPreferences}
              setSelectItems={setSelectedPreferences}
              industryOptions={R.prop('industry', preferences) || []}
              companySizeOptions={R.prop('size', preferences) || []}
              countryOptions={R.prop('country', preferences) || []}
            />
          </React.Fragment>
        )
      case 2:
        return (
          <React.Fragment>
            <Header as="h1">What helpdesk or support platform does your team use?</Header>
            <IntegrationForm
              items={R.prop('helpdesk_integration', preferences) || []}
              selectedItems={selectedPreferences}
              setSelectItems={setSelectedPreferences}
              integrationType={'helpdesk_integration'}
            />
          </React.Fragment>
        )
      case 3:
        return (
          <React.Fragment>
            <Header as="h1">What CRM or marketing automation tools do you use?</Header>
            <IntegrationForm
              items={R.prop('crm_email_integration', preferences) || []}
              selectedItems={selectedPreferences}
              setSelectItems={setSelectedPreferences}
              integrationType={'crm_email_integration'}
            />
          </React.Fragment>
        )
      case 4:
        return (
          <React.Fragment>
            <Header as="h1">
              What other tools do you use that you’d like to connect with Simplesat?
            </Header>
            <IntegrationForm
              items={R.prop('other_integration', preferences) || []}
              selectedItems={selectedPreferences}
              setSelectItems={setSelectedPreferences}
              integrationType={'other_integration'}
            />
          </React.Fragment>
        )
      case 5:
        return (
          <React.Fragment>
            <Header as="h1">Are you interested in any of these features?</Header>
            <GoalPreferencesForm
              items={R.prop('goal', preferences) || []}
              selectedItems={selectedPreferences}
              setSelectItems={setSelectedPreferences}
            />
          </React.Fragment>
        )
      case 6:
        return (
          <React.Fragment>
            <Header as="h1">Customize your branding</Header>
            <BrandOnboardingSettingForm
              brand={brand}
              setBrand={setBrand}
              logos={brandLogos}
              colors={brandColors}
            />
          </React.Fragment>
        )
      default:
        return null
    }
  }

  return (
    <Container>
      <Title title="Onboarding" />
      <LeftPane>
        <div>
          <Image size="small" src={constants.simplesatLogoBlackUrlSvg} alt="Simplesat Logo" />
          <div className="pt-6 pb-4">
            <Progress percent={(currentStep / lastStep) * 100} status="normal" showInfo={false} />
          </div>
          <Transition
            animation="fade right"
            duration={600}
            transitionOnMount
            key={currentStep} // Important to re-trigger animation
          >
            <Form>{renderFormComponent()}</Form>
          </Transition>
        </div>
        <div>
          {currentStep > 1 && (
            <Button
              className="!mt-3"
              basic
              floated="left"
              onClick={(e) => {
                e.target.blur()
                handleStepChange('back')
              }}
            >
              Back
            </Button>
          )}
          <Button
            className="!mt-3"
            primary
            floated="right"
            onClick={(e) => {
              e.target.blur()
              handleStepChange('next')
            }}
          >
            {isPreviewBrand ? 'Finalize!' : 'Continue'}
          </Button>
        </div>
      </LeftPane>
      {isPreviewBrand ? (
        <BrandOnboardingPreview brand={brand} />
      ) : (
        <RightPane selectedPreferences={selectedPreferences} companyName={companyName} />
      )}
    </Container>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/Onboarding/index.js